import { Injectable, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BehaviorSubject, Observable, of, Subject, Subscribable, Subscription } from 'rxjs';
import { map, filter, take, catchError, switchMap, tap } from 'rxjs/operators';

import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';

import { ActivityBlockingService } from '../services/activity-blocking.service';
import { BrowserProviderService } from '../services/browser-provider.service';
import { LoggingService } from '../services/logging.service';

import { SharedSettings } from '../models/shared-settings';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private roles: BehaviorSubject<string[]>;

    private authMessage: BehaviorSubject<{ summary: string, detail: string}>;

    private errState = false;

    private _isReady = new BehaviorSubject<boolean>(false);

    get IsReady(): Observable<boolean> {
        return this._isReady.asObservable();
    }

    get Roles(): Observable<string[]> {
        return this.roles.asObservable().pipe(filter(roles => roles && roles.length > 0));
    }

    get AuthMessage(): Observable<{ summary: string, detail: string}> {
        return this.authMessage.asObservable();
    }

    private _loaded = new Subject<void>();

    // MsalService and MsalBroadcastService are optional because they are not available in the partner-access project,
    // which does not use MSAL for authentication.
    constructor(
            @Optional() private msal: MsalService,
            @Optional() private broadcastService: MsalBroadcastService,
            private http: HttpClient,
            private logger: LoggingService,
            private browser: BrowserProviderService,
            private activityService: ActivityBlockingService,
            private settings: SharedSettings
    ) {
        this.roles = new BehaviorSubject<string[]>([]);
        this.authMessage = new BehaviorSubject<{ summary: string, detail: string}>(null);

        if (!this.msal || !this.broadcastService) {
            this.logger.warning('AuthService initialized without MSAL services. Auth features will be disabled.');
            this._isReady.next(true);
            this._loaded.next();
            return;
        }

        this.broadcastService.msalSubject$.subscribe(event => {
            if (event && (!this.settings.isProduction || event.error))
                console.warn(event);
        });

        this.msal.initialize().pipe(
            this.activityService.Wait('auth service initialization'),
            switchMap(() => this.msal.handleRedirectObservable()),
            switchMap(() => this.broadcastService.inProgress$),
            filter(status => status === InteractionStatus.None),
            map(() => this.msal.instance.getAllAccounts()),
            take(1)
        ).subscribe(accounts => {
            var account = this.filterAccount(accounts);

            if (account) {
                this.logger.info('logged in as ' + account.username);
                this._isReady.next(true);
                this.http.get<string[]>(this.settings.apiBaseUrl + 'users/me/roles').subscribe({
                    next: roles => {
                        this.roles.next(roles);
                    },
                    error: _error => {
                        this.logger.error('error fetching user roles', _error);
                        this.roles.next([]);
                    },
                    complete: () => {
                        this._loaded.next();
                    }
                });
            }
        });
    }

    logout(): void {
        this.http.post(this.settings.apiBaseUrl + 'logout', {})
            .pipe(
                catchError(_ => of({})),
                switchMap(() => this.msal.logoutRedirect())
            )
            .subscribe();
    }

    private filterAccount(accounts: AccountInfo[]): AccountInfo {
        // not logged in
        if (!accounts.length) {
            if (!this.errState) {
                this.errState = true;
                this.activityService.StartWorking();
                this.msal.loginRedirect({
                    redirectStartPage: this.browser.GetLocation().href,
                    scopes: this.settings.authScopes
                });
            }

            return null;
        }
        // non-company account
        else if (!this.settings.validAccountDomains.includes(accounts[0].username.toLowerCase().split('@').at(-1))) {
            if (!this.errState) {
                this.errState = true;
                this.activityService.StartWorking();
                this.authMessage.next({
                    summary: 'Invalid Account',
                    detail: 'PLA Vision may only be accessed via PLA accounts. '
                        + 'You will be signed out and asked to sign in again.',
                });
                setTimeout(() => this.msal.logoutRedirect(), 5000);
            }

            return null;
        }
        // all ok
        else {
            return accounts[0];
        }
}

    getAccount(): Observable<AccountInfo> {
        return this._isReady.pipe(
            filter(is => !!is),
            take(1),
            map(_ => {
                if (!this.msal)
                    return null;

                try {
                    const accounts = this.msal.instance.getAllAccounts();
                    return this.filterAccount(accounts);
                }
                catch (e) {
                    this.logger.warning('Auth Error', { msg: 'error getting user from msal', error: e });
                }

                return null;
            }));
    }

    getUsername(): Observable<string> {
        return this.getAccount().pipe(map(acct => acct?.username));
    }

    getDisplayName(): Observable<string> {
        return this.getAccount().pipe(map(acct => acct?.name));
    }

    getUserRoles(): string[] {
        return this.roles.value;
    }

    isUserInAnyRoles(roles: string[]): Observable<boolean> {
        if (!roles || roles.length === 0)
            return of(true);

        return this.roles.pipe(
            filter(userRoles => userRoles && userRoles.length > 0),
            take(1),
            map(userRoles => roles.some(r => userRoles.some(ur => ur === r)))
        );
    }

    isAuthenticated(): Observable<boolean> {
        return this.getAccount().pipe(map(acct => !!acct));
    }

    forceLogin(): void {
        this.msal.loginRedirect();
    }
}
